import type {
  GeneratedDelivery,
  GenerateDeliveriesResponse,
  SkippedOutlet,
} from '@/features/kitchen/api/generateDispatchDeliveries'

export const SKIP_REASON_STOCK = 'Tidak ada stok dapur untuk sisa rencana toko ini.'
export const SKIP_REASON_NEED_ZERO = 'Sisa rencana HO untuk toko ini sudah terpenuhi.'
export const SKIP_REASON_NO_PLAN_PREFIX = 'Belum ada rencana HO untuk toko ini pada tanggal '

export type SkipClass = 'stock' | 'need_zero' | 'no_plan' | 'other'

export type GenerateCta = 'view_shipment' | 'to_production' | 'close'

export type GenerateResultViewModel = {
  title: string
  summary: string
  tone: 'success' | 'info' | 'warning'
}

export function classifySkipReason(reason: string): SkipClass {
  if (reason === SKIP_REASON_STOCK) return 'stock'
  if (reason === SKIP_REASON_NEED_ZERO) return 'need_zero'
  if (reason.startsWith(SKIP_REASON_NO_PLAN_PREFIX)) return 'no_plan'
  return 'other'
}

export function hasStockSkip(result: {
  skipped_outlets?: SkippedOutlet[]
}): boolean {
  return (result.skipped_outlets ?? []).some(
    (o) => classifySkipReason(o.reason) === 'stock',
  )
}

export function hasHeldLines(result: {
  deliveries?: GeneratedDelivery[] | null
}): boolean {
  return (result.deliveries ?? []).some((d) => (d.heldLines?.length ?? 0) > 0)
}

export function resolveGenerateCtas(result: {
  deliveries?: GeneratedDelivery[] | null
  skipped_outlets?: SkippedOutlet[] | null
}): { primary: GenerateCta; secondary: GenerateCta | null } {
  const hasDeliveries = (result.deliveries ?? []).length >= 1
  const stock = hasStockSkip(result)
  const held = hasHeldLines(result)

  if (hasDeliveries) {
    if (stock || held) {
      return { primary: 'view_shipment', secondary: 'to_production' }
    }
    return { primary: 'view_shipment', secondary: 'close' }
  }

  if (stock) {
    return { primary: 'to_production', secondary: 'close' }
  }

  return { primary: 'close', secondary: null }
}

export function deliveryLineCount(delivery: GeneratedDelivery): number {
  return (delivery.lines ?? []).length
}

export function deliveryQtyTotal(delivery: GeneratedDelivery): number {
  return (delivery.lines ?? []).reduce((sum, line) => sum + line.qty, 0)
}

export function buildGenerateSummary({
  result,
  selectedOutletCount,
}: {
  result: Pick<GenerateDeliveriesResponse, 'deliveries' | 'skipped_outlets'>
  selectedOutletCount: number
}): GenerateResultViewModel {
  const deliveries = result.deliveries ?? []
  const skipped = result.skipped_outlets ?? []
  const hasDeliveries = deliveries.length > 0
  const stock = hasStockSkip(result)
  const held = hasHeldLines({ deliveries })

  let title: string
  if (selectedOutletCount === 1) {
    const storeName =
      deliveries[0]?.store_name ?? skipped[0]?.store_name ?? 'Toko'
    title = `Hasil · ${storeName}`
  } else {
    title = `Hasil generate · ${selectedOutletCount} toko`
  }

  let summary: string
  if (selectedOutletCount === 1 && hasDeliveries) {
    const d = deliveries[0]
    const lines = deliveryLineCount(d)
    const qty = deliveryQtyTotal(d)
    summary = `${d.store_name}: kiriman siap (${lines} baris · qty ${qty}).`
  } else if (hasDeliveries) {
    summary = `${deliveries.length} kiriman dibuat/diperbarui.`
    if (skipped.length > 0) {
      summary += ` ${skipped.length} toko tidak mendapat kiriman baru.`
    }
  } else {
    summary = 'Tidak ada kiriman baru.'
  }

  let tone: GenerateResultViewModel['tone']
  if (stock || held) {
    tone = 'warning'
  } else if (hasDeliveries) {
    tone = 'success'
  } else {
    tone = 'info'
  }

  return { title, summary, tone }
}
